import { useState, useEffect } from 'react'
import {
  Key,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Calendar,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { APIKey, CreateAPIKeyData } from '@/types'
import { saleShopService } from '@/services/saleShopService'
import { formatDistanceToNow } from 'date-fns'

interface APIKeyManagementProps {
  saleShopId: number
  saleShopName: string
}

export default function APIKeyManagement({ saleShopId, saleShopName }: APIKeyManagementProps) {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [deleting, setDeleting] = useState<number | null>(null)
  const [showSecret, setShowSecret] = useState<number | null>(null)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState<string>('')

  useEffect(() => {
    fetchAPIKeys()
  }, [saleShopId])

  const fetchAPIKeys = async () => {
    setLoading(true)
    try {
      const response = await saleShopService.getAPIKeys(saleShopId)
      if (response.success) {
        setApiKeys(response.data)
      }
    } catch (error: any) {
      setError(saleShopService.handleError(error))
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAPIKey = async () => {
    setCreating(true)
    setError('')
    try {
      const data: CreateAPIKeyData = {
        sale_shop: saleShopId,
        is_active: true,
      }

      const response = await saleShopService.createAPIKey(saleShopId, data)
      if (response.success) {
        setApiKeys([response.data])
        setSuccess('API key created successfully!')
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (error: any) {
      setError(saleShopService.handleError(error))
    } finally {
      setCreating(false)
    }
  }

  const handleRegenerateAPIKey = async () => {
    if (
      !window.confirm(
        'Are you sure you want to regenerate the API key? This will invalidate the current key.'
      )
    ) {
      return
    }

    setRegenerating(true)
    setError('')
    try {
      const response = await saleShopService.regenerateAPIKey(saleShopId, {
        confirm_regenerate: true,
      })
      if (response.success) {
        setApiKeys([response.data])
        setSuccess('API key regenerated successfully!')
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (error: any) {
      setError(saleShopService.handleError(error))
    } finally {
      setRegenerating(false)
    }
  }

  const handleDeleteAPIKey = async (apiKeyId: number) => {
    if (
      !window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')
    ) {
      return
    }

    setDeleting(apiKeyId)
    setError('')
    try {
      const response = await saleShopService.deleteAPIKey(apiKeyId)
      if (response.success) {
        setApiKeys([])
        setSuccess('API key deleted successfully!')
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (error: any) {
      setError(saleShopService.handleError(error))
    } finally {
      setDeleting(null)
    }
  }

  const copyToClipboard = async (text: string, keyType: 'public' | 'secret') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(keyType)
      setTimeout(() => setCopiedKey(null), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  const toggleSecretVisibility = (apiKeyId: number) => {
    setShowSecret(showSecret === apiKeyId ? null : apiKeyId)
  }

  const getStatusColor = (apiKey: APIKey) => {
    if (apiKey.is_expired) return 'text-red-400 bg-red-500/20 border-red-500/30'
    if (!apiKey.is_active) return 'text-gray-400 bg-gray-500/20 border-gray-500/30'
    return 'text-green-400 bg-green-500/20 border-green-500/30'
  }

  const getStatusText = (apiKey: APIKey) => {
    if (apiKey.is_expired) return 'Expired'
    if (!apiKey.is_active) return 'Inactive'
    return 'Active'
  }

  if (loading) {
    return (
      <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
        <div className="p-6 card-content text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-white/60" />
          <p className="text-white/80">Loading API keys...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
        <div className="p-4 sm:p-6 card-content">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 lg:gap-6">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center space-x-3">
                <span className="truncate">API Keys</span>
              </h2>
              <p className="text-white/70 mt-1 text-sm sm:text-base truncate">
                Manage {saleShopName} API keys
              </p>
            </div>

            <div className="flex-shrink-0 w-full lg:w-auto">
              {apiKeys.length === 0 ? (
                <Button
                  onClick={handleCreateAPIKey}
                  disabled={creating}
                  className="btn-gradient w-full lg:w-auto"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin" />
                      <span className="hidden sm:inline">Creating...</span>
                      <span className="sm:hidden">Creating...</span>
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      <span className="hidden sm:inline">Create API Key</span>
                      <span className="sm:hidden">Create Key</span>
                    </>
                  )}
                </Button>
              ) : (
                <div className="flex flex-col xs:flex-row gap-2 xs:gap-3">
                  <Button
                    onClick={handleRegenerateAPIKey}
                    disabled={regenerating}
                    variant="outline"
                    className="border-gray-600 text-white hover:bg-white/10 w-full xs:w-auto"
                  >
                    {regenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin" />
                        <span className="hidden sm:inline">Regenerating...</span>
                        <span className="sm:hidden">Regenerating...</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        <span className="hidden sm:inline">Regenerate</span>
                        <span className="sm:hidden">Regenerate</span>
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleDeleteAPIKey(apiKeys[0].id)}
                    disabled={deleting === apiKeys[0].id}
                    variant="outline"
                    className="text-red-200 border-red-500/30 hover:bg-red-500/20 w-full xs:w-auto"
                  >
                    {deleting === apiKeys[0].id ? (
                      <>
                        <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin" />
                        <span className="hidden sm:inline">Deleting...</span>
                        <span className="sm:hidden">Deleting...</span>
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        <span className="hidden sm:inline">Delete</span>
                        <span className="sm:hidden">Delete</span>
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center space-x-3">
          <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-500/20 border border-green-500/30 rounded-lg flex items-center space-x-3">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          <p className="text-green-200">{success}</p>
        </div>
      )}

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
          <div className="p-6 card-content text-center py-12">
            <Key className="h-12 w-12 mx-auto mb-4 text-white/60" />
            <p className="text-white/80 mb-4">No API keys found</p>
            <p className="text-sm text-white/60 mb-6">
              Create an API key to start integrating with your sales channel
            </p>
            <Button onClick={handleCreateAPIKey} disabled={creating} className="btn-gradient">
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Create API Key
                </>
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {apiKeys.map(apiKey => (
            <div
              key={apiKey.id}
              className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50"
            >
              <div className="p-6 card-content">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <Key className="h-5 w-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">API Key</h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(apiKey)}`}
                        >
                          {getStatusText(apiKey)}
                        </span>
                        {apiKey.is_expired && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-orange-400 bg-orange-500/20 border border-orange-500/30">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Expired
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Public Key */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Public Key
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={apiKey.public_key}
                        readOnly
                        className="input flex-1 py-2.5 px-3 text-sm font-mono bg-white/5 border border-gray-600 text-white"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(apiKey.public_key, 'public')}
                        className="px-3 border-gray-600 text-white hover:bg-white/10"
                      >
                        {copiedKey === 'public' ? (
                          <CheckCircle className="h-4 w-4 text-green-400" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Secret Key */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Secret Key
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type={showSecret === apiKey.id ? 'text' : 'password'}
                        value={apiKey.secret_key}
                        readOnly
                        className="input flex-1 py-2.5 px-3 text-sm font-mono bg-white/5 border border-gray-600 text-white"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleSecretVisibility(apiKey.id)}
                        className="px-3 border-gray-600 text-white hover:bg-white/10"
                      >
                        {showSecret === apiKey.id ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(apiKey.secret_key, 'secret')}
                        className="px-3 border-gray-600 text-white hover:bg-white/10"
                      >
                        {copiedKey === 'secret' ? (
                          <CheckCircle className="h-4 w-4 text-green-400" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-white/60 mt-1">
                      Keep your secret key secure and never share it publicly
                    </p>
                  </div>
                </div>

                {/* Key Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-4 border-t border-gray-600">
                  <div className="flex items-center space-x-2 text-sm text-white/60">
                    <Calendar className="h-4 w-4" />
                    <span>
                      Created{' '}
                      {formatDistanceToNow(new Date(apiKey.created_at), { addSuffix: true })}
                    </span>
                  </div>

                  {apiKey.last_used && (
                    <div className="flex items-center space-x-2 text-sm text-white/60">
                      <Clock className="h-4 w-4" />
                      <span>
                        Last used{' '}
                        {formatDistanceToNow(new Date(apiKey.last_used), { addSuffix: true })}
                      </span>
                    </div>
                  )}

                  {apiKey.expires_at && (
                    <div className="flex items-center space-x-2 text-sm text-white/60">
                      <AlertTriangle className="h-4 w-4" />
                      <span>
                        Expires{' '}
                        {formatDistanceToNow(new Date(apiKey.expires_at), { addSuffix: true })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
